import { grey, blue, purple } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import fi from 'date-fns/locale/fi'
import { Chart, ChartData, ChartDataset, ChartOptions } from 'chart.js'
import 'chartjs-adapter-date-fns'
import {
  getDifferenceOfDateRangeInHours,
  humanizeDateRange,
  humanizeTimestamp,
  isDifferenceMoreThanMinutes
} from '../utils/date'
import LoadingState from './LoadingState'
import _ from 'lodash'
import LineChart from './LineChart'
import { useTheme } from '@mui/material'
import {
  DateRange,
  RuuviTagCondition,
  RuuviTagConditionField,
  RuuviTagConditionQuery,
  RuuviTagConditionStatistic
} from '../types'
import TimePeriod from '../enums/TimePeriod'
import { setDateRange, setTimePeriod } from '../redux/slices/query'
import { useDispatch } from 'react-redux'
import zoom from 'chartjs-plugin-zoom'

Chart.register(zoom)
export interface RuuviTagConditionChart {
  ruuviTagConditions: RuuviTagCondition[]
  ruuviTagConditionQuery: RuuviTagConditionQuery
  ruuviTagConditionField: RuuviTagConditionField
  ruuviTagConditionStatistics?: RuuviTagConditionStatistic[]

  /**
   * The date range used for the chart.
   */
  dateRange?: DateRange

  chartOptions?: ChartOptions<'line'>
  chartWrapperHeight?: string
  loading?: boolean
}

export default function RuuviTagConditionChart({
  ruuviTagConditions,
  ruuviTagConditionQuery,
  ruuviTagConditionField,
  ruuviTagConditionStatistics,
  dateRange,
  chartOptions,
  chartWrapperHeight = '320px',
  loading = false
}: RuuviTagConditionChart) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The difference of date range as hours.
   */
  const differenceOfDateRangeInHours = getDifferenceOfDateRangeInHours(
    dateRange ? dateRange.from : ruuviTagConditionQuery.from,
    dateRange ? dateRange.to : ruuviTagConditionQuery.to
  )

  /**
   * Update the date range.
   */
  function updateDateRange({ chart }: { chart: Chart }) {
    const { min, max } = chart.scales.x

    dispatch(setTimePeriod(TimePeriod.CUSTOM))
    dispatch(
      setDateRange({
        from: new Date(parseInt(min.toString())),
        to: new Date(parseInt(max.toString()))
      })
    )
  }

  const chartData: ChartData = {
    datasets: makeChartDatasets()
  }

  const defaultChartOptions: ChartOptions = {
    plugins: {
      zoom: {
        zoom: {
          drag: {
            enabled: true
          },
          mode: 'x',
          onZoomComplete: updateDateRange
        }
      },
      title: {
        display: true,
        position: 'bottom',
        padding: {
          top: 24
        },
        text: `${humanizeDateRange(
          dateRange ? dateRange.from : ruuviTagConditionQuery.from,
          dateRange ? dateRange.to : ruuviTagConditionQuery.to
        )}`,
        font: {
          // @ts-expect-error typed incorrectly
          weight: '600',
          size: 14
        },
        color: theme.palette.text.primary
      },
      legend: {
        display: true,
        labels: {
          boxWidth: 24,
          boxHeight: 1,
          padding: 24,
          font: {
            // @ts-expect-error typed incorrectly
            weight: '600',
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems): string => {
            if (tooltipItems.length) {
              // @ts-expect-error typed incorrectly
              return humanizeTimestamp(tooltipItems[0].raw.x, true)
            }

            return ''
          }
        }
      },
      decimation: {
        enabled: false,
        algorithm: 'min-max'
      }
    },
    elements: {
      point: {
        pointStyle: 'point',
        radius: 0
      },
      line: {
        borderWidth: 1
      }
    },
    scales: {
      x: {
        type: 'time',
        display: true,
        // @ts-expect-error typed incorrectly
        min: dateRange ? dateRange.from : ruuviTagConditionQuery.from,
        // @ts-expect-error typed incorrectly
        max: dateRange ? dateRange.to : ruuviTagConditionQuery.to,
        time: {
          unit:
            differenceOfDateRangeInHours <= 3
              ? 'minute'
              : differenceOfDateRangeInHours <= 24
                ? 'hour'
                : 'day',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
            day: 'd.M'
          }
        },
        adapters: {
          date: {
            locale: fi
          }
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          source: 'auto'
        },
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        },
        color: '#fff'
      },
      y: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(255, 255, 255, 0.0375)',
          // @ts-expect-error typed incorrectly
          fontColor: '#fff',
          drawBorder: false
        }
      }
    }
  }

  chartOptions = _.merge(defaultChartOptions, chartOptions)

  function makeChartDatasets(): ChartDataset[] {
    const datasets: ChartDataset[] = []

    if (ruuviTagConditionStatistics !== undefined) {
      for (const statistic of ruuviTagConditionStatistics) {
        datasets.push(makeChartDataset(ruuviTagConditionField, statistic))
      }
    } else {
      datasets.push(makeChartDataset(ruuviTagConditionField))
    }

    return datasets
  }

  /**
   * Make the dataset.
   */
  function makeChartDataset(
    field: RuuviTagConditionField,
    statistic?: RuuviTagConditionStatistic
  ): ChartDataset {
    const label = makeDatasetLabel(field, statistic)
    const color = makeDatasetColor(field, statistic)
    const data = makeDatasetData(field, statistic)

    return {
      label,
      backgroundColor: color,
      borderColor: color,
      tension: 0.4,
      spanGaps: false,
      segment: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        borderColor: (ctx: any) => {
          const skipLine = isDifferenceMoreThanMinutes(
            new Date(ctx.p0.raw.x),
            new Date(ctx.p1.raw.x),
            15
          )

          return skipLine ? 'transparent' : undefined
        }
      },
      // @ts-expect-error typed incorrectly
      data: data
    }
  }

  /**
   * Make the key.
   */
  function makeKey(
    field: RuuviTagConditionField,
    statistic?: RuuviTagConditionStatistic
  ): keyof RuuviTagCondition {
    return statistic
      ? (_.camelCase(`${field}_${statistic}`) as keyof RuuviTagCondition)
      : (field as keyof RuuviTagCondition)
  }

  /**
   * Make the label for the dataset.
   */
  function makeDatasetLabel(
    field: RuuviTagConditionField,
    statistic?: RuuviTagConditionStatistic
  ): string {
    return statistic ? `${t(field)} (${t(statistic)})` : field
  }

  /**
   * Make the color for the dataset.
   */
  function makeDatasetColor(field: RuuviTagConditionField, statistic?: RuuviTagConditionStatistic) {
    let weight: 'A100' | 'A700' | '500'
    switch (statistic) {
      case 'min':
        weight = 'A100'
        break
      case 'max':
        weight = 'A700'
        break
      case 'avg':
      default:
        weight = '500'
        break
    }

    // @see https://mui.com/material-ui/customization/color/#color-palette
    switch (field) {
      case 'humidity':
        return grey[weight]
      case 'pressure':
      case 'rssi':
        return purple[weight]
      case 'temperature':
      default:
        return blue[weight]
    }
  }

  /**
   * Make the data for the dataset.
   */
  function makeDatasetData(field: RuuviTagConditionField, statistic?: RuuviTagConditionStatistic) {
    const key = makeKey(field, statistic)

    return ruuviTagConditions.map((ruuviTagCondition) => {
      return {
        x: ruuviTagCondition.time,
        y: ruuviTagCondition[key] as number
      }
    })
  }

  return (
    <>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          <LineChart data={chartData} options={chartOptions} wrapperHeight={chartWrapperHeight} />
        </>
      )}
    </>
  )
}
